import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Phone, Inbox } from "lucide-react";
import { api } from "@/lib/api";

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.listInquiries().then((d) => { setItems(d.items || []); if (d.items?.length) setSelected(d.items[0]); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-slate-800" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-display font-bold text-slate-800">Inquiries</h1>
        <p className="text-slate-500">Customer messages from your website contact form.</p>
      </div>
      {items.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl glass-panel-dark border-slate-200">
          <Inbox className="w-12 h-12 mx-auto text-slate-500" />
          <div className="mt-3 font-display font-semibold text-slate-800">No inquiries yet</div>
          <p className="text-slate-500 mt-1 text-sm">When visitors submit the contact form, you'll see them here.</p>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-1 rounded-2xl glass-panel-dark border-slate-200 overflow-hidden max-h-[70vh] overflow-y-auto">
            {items.map((i) => (
              <button key={i.id} onClick={() => setSelected(i)} className={`w-full text-left p-4 border-b border-slate-100 hover:bg-orange-50/30 transition-colors ${selected?.id === i.id ? "bg-orange-50/50 border-l-4 border-l-[#ea580c]" : "border-l-4 border-l-transparent"}`} data-testid={`inquiry-row-${i.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className={`font-semibold ${selected?.id === i.id ? "text-slate-900" : "text-slate-700"}`}>{i.name}</div>
                  <Badge className="bg-[#ea580c] text-white border-none shadow-sm uppercase tracking-wider text-[9px] font-bold px-2 py-0.5">{i.status}</Badge>
                </div>
                <div className="text-xs text-slate-500 truncate mb-1">{i.tour_interest || "General Inquiry"} · {new Date(i.created_at).toLocaleDateString()}</div>
                <div className="text-xs text-slate-400 truncate">{i.message}</div>
              </button>
            ))}
          </Card>
          <Card className="lg:col-span-2 rounded-2xl glass-panel-dark border-slate-200 p-8 shadow-sm">
            {selected ? (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-start justify-between pb-6 border-b border-slate-100 mb-6">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-slate-800 tracking-tight">{selected.name}</h2>
                    <div className="text-sm text-slate-500 mt-1">{new Date(selected.created_at).toLocaleString()}</div>
                  </div>
                  <Badge className="bg-[#ea580c] text-white border-none shadow-sm uppercase tracking-wider text-xs font-bold px-3 py-1">{selected.status}</Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-8 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                  {selected.email && <div className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-[#ea580c]" /><a className="text-slate-700 font-medium hover:text-[#ea580c] transition-colors" href={`mailto:${selected.email}`}>{selected.email}</a></div>}
                  {selected.phone && <div className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-[#ea580c]" /><a className="text-slate-700 font-medium hover:text-[#ea580c] transition-colors" href={`tel:${selected.phone}`}>{selected.phone}</a></div>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm mb-8">
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"><div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Tour Interest</div><div className="font-semibold text-slate-800">{selected.tour_interest || "—"}</div></div>
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"><div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Travel Date</div><div className="font-semibold text-slate-800">{selected.travel_date || "—"}</div></div>
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"><div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Travelers</div><div className="font-semibold text-slate-800">{selected.travelers || "—"}</div></div>
                </div>
                <div className="p-6 bg-orange-50/30 border border-orange-100 rounded-xl">
                  <div className="text-xs font-semibold text-[#ea580c] uppercase tracking-wider mb-3">Message</div>
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                </div>
              </div>
            ) : <div className="text-slate-500 text-center py-20">Select an inquiry from the left to view details</div>}
          </Card>
        </div>
      )}
    </div>
  );
}
